import * as React from 'react';
import {Mutation, Query} from 'react-apollo';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {MessageContainer} from 'shells/message/message-container';
import {Body} from 'shells/typography/body';
import {Title} from 'shells/typography/title';

import type {
    GetEmailStats as GetEmailStatsQuery,
    ResumeAutomatedEmails as resumeAutomatedEmailsMutation,
} from 'nutshell-graphql-types';

import RESUME_AUTOMATED_EMAILS from './email-health/graphql/mutations/resume-automated-emails.graphql';
import GET_EMAIL_STATS from './email-health/graphql/queries/email-stats-query.graphql';

import styles from './automated-email-paused-banner.module.css';

class EmailStatQuery extends Query<GetEmailStatsQuery, Record<any, any>> {}

class ResumeAutomatedEmails extends Mutation<resumeAutomatedEmailsMutation, Record<any, any>> {}

export function AutomatedEmailPausedBanner() {
    const [isConfirmationOpen, setIsConfirmationOpen] = React.useState<boolean>(false);

    return (
        <EmailStatQuery query={GET_EMAIL_STATS}>
            {({data}) => {
                if (data && data.emailStats && data.emailStats.isAutomatedSendingPaused) {
                    return (
                        <ResumeAutomatedEmails
                            mutation={RESUME_AUTOMATED_EMAILS}
                            refetchQueries={['GetEmailStats']}
                        >
                            {(resumeAutomatedEmails, {loading: resumeMutationInFlight}) => (
                                <div className={styles['automated-email-paused-banner']}>
                                    <MessageContainer>
                                        <FlashBanner
                                            animate={false}
                                            type='warning'
                                            hasBorder={true}
                                        >
                                            <div className='mr-16'>
                                                <WarningIcon size={32} />
                                            </div>
                                            <div className={styles['text-container']}>
                                                <Title>
                                                    You’ve paused all outgoing automated emails
                                                </Title>
                                                <Body as='div'>
                                                    Any messages that would normally send during
                                                    this period won’t be sent.
                                                </Body>
                                            </div>
                                            <Button
                                                variant='text-primary'
                                                size='big'
                                                onClick={() => setIsConfirmationOpen(true)}
                                            >
                                                Turn sending back on
                                            </Button>
                                        </FlashBanner>
                                    </MessageContainer>
                                    {isConfirmationOpen ? (
                                        <ConfirmationDialog
                                            title='Ready to turn automated emails back on?'
                                            message='Any emails that would have sent while sending was paused will be rescheduled to send in 15 minutes.'
                                            onConfirm={() => {
                                                resumeAutomatedEmails().then(() => {
                                                    setIsConfirmationOpen(false);
                                                });
                                            }}
                                            onCancel={() => setIsConfirmationOpen(false)}
                                            confirmText={
                                                resumeMutationInFlight
                                                    ? 'Resuming automated emails…'
                                                    : 'Yes, resume sending automations'
                                            }
                                            cancelText='Not right now'
                                        />
                                    ) : undefined}
                                </div>
                            )}
                        </ResumeAutomatedEmails>
                    );
                }

                return null;
            }}
        </EmailStatQuery>
    );
}
